import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { company } from '~/company/Company';
import Typography, { VARIANT } from '~/components/isolated/Typography';
import CDPrice from '~/components/Price/CDPrice/CDPrice';
import { cartStore, mainStore } from '~/stores';
import { pluralize } from '~/utils/pluralize';
import styles from './OrderBreakdown.module.scss';

/**
 * OrderBreakdown component
 * Displays price breakdown: Items, Discount, Promocode discount, VAT
 */
const OrderBreakdown = observer(() => {
  const { t } = useTranslation();

  const totalCartPrice = cartStore.totalCartPrice;
  const selectedItemsTotalCount = cartStore.selectedItemsTotalCount;
  const hasDiscount = Number(totalCartPrice.discount) > 0;
  const hasPromoDiscount = Number(totalCartPrice.promocodeDiscount) > 0;
  const hasTax = totalCartPrice.taxAmount > 0;

  const discountPercent =
    hasDiscount && Number(totalCartPrice.base) > 0
      ? Math.round(
          (Number(totalCartPrice.discount) / Number(totalCartPrice.base)) * 100,
        )
      : 0;

  const promoDiscountPercent =
    hasPromoDiscount && Number(totalCartPrice.base) > 0
      ? Math.round(
          (Number(totalCartPrice.promocodeDiscount) /
            Number(totalCartPrice.base)) *
            100,
        )
      : 0;

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <Typography variant={VARIANT['Text 200/Medium']}>
          {selectedItemsTotalCount}{' '}
          {pluralize(selectedItemsTotalCount, [
            t('item'),
            t('items_few'),
            t('items'),
          ])}
        </Typography>
        <CDPrice
          price={totalCartPrice.base}
          classes={{ classNameCurrentPrice: styles.value }}
        />
      </div>

      {hasDiscount && (
        <div className={styles.row}>
          <Typography variant={VARIANT['Text 200/Medium']}>
            {t('discount')}
          </Typography>
          <span className={styles.discountGroup}>
            <CDPrice
              price={`-${totalCartPrice.discount}`}
              classes={{ classNameCurrentPrice: styles.discountValue }}
            />
            {company.is('mcosmetic') && discountPercent > 0 && (
              <span className={styles.discountValue}>
                (-{discountPercent}%)
              </span>
            )}
          </span>
        </div>
      )}

      {hasPromoDiscount && (
        <div className={styles.row}>
          <Typography variant={VARIANT['Text 200/Medium']}>
            {t('promocode')}
          </Typography>
          <span className={styles.discountGroup}>
            <CDPrice
              price={`-${totalCartPrice.promocodeDiscount}`}
              classes={{
                classNameCurrentPrice: styles.discountValue,
              }}
            />
            {company.is('mcosmetic') && promoDiscountPercent > 0 && (
              <span className={styles.discountValue}>
                (-{promoDiscountPercent}%)
              </span>
            )}
          </span>
        </div>
      )}

      {hasTax && (
        <div className={styles.row}>
          <Typography variant={VARIANT['Text 200/Medium']}>
            {t('phrases:vat', { percent: totalCartPrice.taxPercent })}
          </Typography>
          <CDPrice
            price={mainStore.convertPenceToPounds(totalCartPrice.taxAmount)}
            classes={{ classNameCurrentPrice: styles.value }}
          />
        </div>
      )}
    </div>
  );
});

export default OrderBreakdown;
